import type { MaybeRefOrGetter, Ref } from 'vue';
import type { NestedSelectMenuItem } from './NestedSelectMenu.vue';
import type { FlattenedItem } from './utils';
import { computed, nextTick, toValue } from 'vue';
import { hasChildren } from './utils';

type HighlightedNestedSelectMenuValue = NestedSelectMenuItem | FlattenedItem;

export interface UseNestedSelectMenuKeyboardOptions<T extends HighlightedNestedSelectMenuValue = NestedSelectMenuItem> {
	isRtl: MaybeRefOrGetter<boolean>;
	highlightedItem: Ref<T | null>;
	expandedItem: Ref<NestedSelectMenuItem | null>;
	nestedMenuRef: Ref<{ focus: () => void } | null>;
	onEscape: () => void;
	onCollapse?: () => void;
	onSelectHighlighted?: (item: T) => void;
}

function isFlattenedItem(item: HighlightedNestedSelectMenuValue): item is FlattenedItem {
	return 'item' in item && 'pathIds' in item;
}

function getMenuItem(item: HighlightedNestedSelectMenuValue): NestedSelectMenuItem {
	return isFlattenedItem(item) ? item.item : item;
}

export function useNestedSelectMenuKeyboard<T extends HighlightedNestedSelectMenuValue = NestedSelectMenuItem>(options: UseNestedSelectMenuKeyboardOptions<T>) {
	const expandKey = computed(() => toValue(options.isRtl) ? 'ArrowLeft' : 'ArrowRight');
	const collapseKey = computed(() => toValue(options.isRtl) ? 'ArrowRight' : 'ArrowLeft');

	async function focusNestedMenuAfterExpansion() {
		await nextTick();
		options.nestedMenuRef.value?.focus();
	}

	function expandHighlightedItem(item: NestedSelectMenuItem) {
		options.expandedItem.value = item;
		void focusNestedMenuAfterExpansion();
	}

	function handleKeydown(event: KeyboardEvent) {
		const highlightedItem = options.highlightedItem.value;
		const highlightedMenuItem = highlightedItem ? getMenuItem(highlightedItem) : null;

		if (event.key === expandKey.value && highlightedMenuItem && hasChildren(highlightedMenuItem)) {
			event.preventDefault();
			expandHighlightedItem(highlightedMenuItem);
			return;
		}

		if (event.key === collapseKey.value && options.onCollapse) {
			event.preventDefault();
			options.onCollapse();
			return;
		}

		if (event.key === 'Escape') {
			event.preventDefault();
			options.onEscape();
			return;
		}

		if (event.key === 'Enter') {
			if (!highlightedItem || !highlightedMenuItem) return;

			if (isFlattenedItem(highlightedItem)) {
				event.preventDefault();
				options.onSelectHighlighted?.(highlightedItem);
				return;
			}

			if (hasChildren(highlightedMenuItem) && !highlightedMenuItem.selectable) {
				event.preventDefault();
				expandHighlightedItem(highlightedMenuItem);
				return;
			}

			event.preventDefault();

			if (hasChildren(highlightedMenuItem) && highlightedMenuItem.selectable) {
				options.expandedItem.value = highlightedMenuItem;
			}

			options.onSelectHighlighted?.(highlightedItem);
		}
	}

	return { handleKeydown, expandKey, collapseKey };
}
